﻿import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";

function SchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Kozijn- en raamreparatie in Tongelre, Eindhoven",
    description:
      "Kozijn, raam en schuifpui reparatie in Tongelre, Lakerlopen en 't Hofke. Vaste tarieven, 10 jaar garantie, ISDE-subsidie hulp.",
    url: "https://ramen-repair-eindhoven.nl/wijken/tongelre/",
    isPartOf: {
      "@type": "WebSite",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Werkgebied",
        item: "https://ramen-repair-eindhoven.nl/wijken/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Tongelre",
        item: "https://ramen-repair-eindhoven.nl/wijken/tongelre/",
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Ramen Repair Eindhoven",
    url: "https://ramen-repair-eindhoven.nl/",
    telephone: "+31407987470",
    areaServed: {
      "@type": "Place",
      name: "Tongelre, Eindhoven",
    },
    serviceType: [
      "Kozijnreparatie",
      "Raamreparatie",
      "Schuifpui reparatie",
      "Glas vervangen",
      "Slot- en cilindervervanging",
      "Velux en dakraam reparatie",
      "Inbraakschade reparatie",
      "Raamkruk vervangen",
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
    </>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Kruimelpad" className="bg-white border-b border-brand-border">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3">
        <ol className="flex items-center gap-2 text-sm font-geist text-brand-text/50 flex-wrap">
          <li>
            <a href="/" className="hover:text-brand-blue transition-colors">Home</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <a href="/wijken/" className="hover:text-brand-blue transition-colors">Werkgebied</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <span className="text-brand-text">Tongelre</span>
          </li>
        </ol>
      </div>
    </nav>
  );
}

function SectionHero() {
  return (
    <section className="bg-brand-text">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-16 md:py-24 lg:py-32">
        <div className="max-w-3xl">
          <h1 className="font-geist font-bold text-3xl md:text-4xl lg:text-5xl text-white tracking-tight mb-5">
            Kozijn- en raamreparatie in Tongelre, Eindhoven
          </h1>
          <p className="font-geist text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
            Specialist in kozijn-, raam- en schuifpui reparatie in heel Tongelre. 48u-spoedservice, 10 jaar garantie.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href="tel:+31407987470"
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-text font-geist font-semibold text-sm px-6 py-3.5 rounded-md hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-base"></i>
              Bel 040 798 7470
            </a>
            <a
              href="/contact/"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-geist font-medium text-sm px-6 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-list-line w-5 h-5 flex items-center justify-center text-base"></i>
              Gratis offerte
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionOverTongelre() {
  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Over Tongelre en onze werkzaamheden
      </h2>
      <div className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed space-y-4">
        <p>
          Tongelre telt veel jaren &apos;50-&apos;80 woningen in Lakerlopen, &apos;t Hofke en Doornakkers. Houten en kunststof kozijnen met versleten scharnieren en sluitmechanismen vormen hier het dagelijkse werk van onze monteurs. Wij vervangen glas inclusief HR++ en helpen gratis met de ISDE-subsidie aanvraag als u uw kozijnen en isolatieglas laat plaatsen.
        </p>
        <p>
          Ook in Tongelresche Akkers en Karpen repareren wij dagelijks draaikiepramen, schuifpuien en dakramen. Onze monteurs kennen de bouwstijlen en weten precies welke materialen passen bij de karakteristieke rijtjeshuizen en twee-onder-een-kapwoningen die hier zo typerend zijn. Geen voorrijkosten, vaste prijzen en altijd schriftelijke garantie.
        </p>
      </div>
    </section>
  );
}

function SectionDiensten() {
  const diensten = [
    { label: "Kozijnreparatie (PVC, hout, aluminium)", href: "/kozijn-reparatie/" },
    { label: "Schuifpui reparatie en afstelling", href: "/schuifpui-reparatie/" },
    { label: "Draaikiepraam reparatie", href: "/draaikiepraam-reparatie/" },
    { label: "Glas vervangen inclusief HR++", href: "/glas-vervangen/" },
    { label: "Slot- en cilindervervanging", href: "/slot-cilinder-vervangen/" },
    { label: "Velux dakraam reparatie", href: "/velux-reparatie/" },
    { label: "Inbraakschade reparatie 24/7", href: "/inbraakschade-reparatie/" },
    { label: "Raamkruk vervangen", href: "/raamkruk-vervangen/" },
  ];

  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Onze diensten in Tongelre
      </h2>
      <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-6">
        Wij leveren alle kozijn-, raam- en schuifpuidiensten in Tongelre. Of u in een karakteristiek jaren &apos;50-huis in Lakerlopen woont of een jaren &apos;80-woning in &apos;t Hofke, onze vakmannen zijn binnen 48 uur bij u op locatie. Hieronder vindt u een overzicht van onze werkzaamheden in deze wijk :
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6">
        {diensten.map((d) => (
          <li key={d.href}>
            <a
              href={d.href}
              className="group flex items-center gap-3 bg-white border border-brand-border/40 rounded-lg px-4 py-3 hover:border-brand-yellow/50 hover:shadow-md transition-all duration-200"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-yellow/15 shrink-0">
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-brand-yellow text-sm group-hover:translate-x-0.5 transition-transform"></i>
              </span>
              <span className="font-geist text-sm md:text-base text-brand-text group-hover:text-brand-yellow transition-colors">
                {d.label}
              </span>
            </a>
          </li>
        ))}
      </ul>
      <p className="font-geist text-sm md:text-base text-brand-text/60 leading-relaxed">
        Elke dienst wordt uitgevoerd door gecertificeerde monteurs met ervaring in zowel jaren &apos;50-&apos;80 woningen als recentere bouw. Wij werken met materialen die passen bij uw woningtype en leveren altijd schriftelijk werk met een transparante prijsopgave vooraf. Vraag vrijblijvend een offerte aan via ons contactformulier.
      </p>
    </section>
  );
}

function SectionWaaromWij() {
  const points = [
    { text: "ISDE-subsidie hulp gratis bij glasvervanging en kozijnrenovatie" },
    { text: "Ervaring met jaren &apos;50-&apos;80 kozijnen, scharnieren en beslag" },
    { text: "Vaste tarieven : geen verrassingen, prijsopgave vooraf" },
    { text: "10 jaar garantie schriftelijk op alle reparaties" },
  ];

  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Waarom Ramen Repair in Tongelre ?
      </h2>
      <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-6">
        Als lokale vakman uit Eindhoven kennen wij Tongelre door en door. Wij weten welke kozijnsoorten hier voorkomen, van de authentieke houten ramen in Lakerlopen tot de kunststof kozijnen in Doornakkers en de gevarieerde bouw in &apos;t Hofke. Daardoor werken wij sneller, efficiënter en met de juiste materialen voor elk type woning.
      </p>
      <ul className="space-y-3">
        {points.map((p) => (
          <li key={p.text} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-brand-yellow/15 shrink-0 mt-0.5">
              <i className="ri-check-line w-4 h-4 flex items-center justify-center text-brand-yellow text-sm"></i>
            </span>
            <span className="font-geist text-sm md:text-base text-brand-text/80 leading-relaxed">
              {p.text}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionWijkenBinnen() {
  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Deelgebieden binnen Tongelre waar wij actief zijn
      </h2>
      <div className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed space-y-4">
        <p>
          Wij werken in alle deelgebieden van Tongelre : Lakerlopen, &apos;t Hofke, Doornakkers, Tongelresche Akkers en Karpen. Geen voorrijkosten binnen het werkgebied, 48u-spoedservice mogelijk.
        </p>
        <p>
          Lakerlopen en Tongelresche Akkers kenmerken zich door veel karakteristieke jaren &apos;50-&apos;60 woningen met houten kozijnen die regelmatig onderhoud vragen. &apos;t Hofke en Doornakkers hebben veel jaren &apos;70-&apos;80 bouw met kunststof kozijnen en schuifpuien. Voor elk type woning en elk bouwjaar hebben wij de juiste expertise en materialen in huis om uw kozijnen weer perfect te laten functioneren.
        </p>
      </div>
    </section>
  );
}

function SectionCTA() {
  return (
    <section className="bg-brand-yellow">
      <div className="max-w-[960px] mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
        <h2 className="font-geist font-bold text-2xl md:text-3xl lg:text-4xl text-brand-text tracking-tight mb-4">
          Direct hulp nodig in Tongelre ?
        </h2>
        <p className="font-geist text-base md:text-lg text-brand-text/70 mb-7 max-w-xl mx-auto">
          Bel ons direct voor een spoedklus of plan een gratis offerte-afspraak in. Wij zijn binnen 48 uur bij u op locatie in Tongelre.
        </p>
        <a
          href="tel:+31407987470"
          className="inline-flex items-center justify-center gap-2 bg-brand-text text-white font-geist font-semibold text-sm px-8 py-4 rounded-md hover:bg-brand-text/90 transition-colors whitespace-nowrap"
        >
          <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-base"></i>
          Bel 040 798 7470
        </a>
      </div>
    </section>
  );
}

export default function TongelrePage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen">
      <title>Kozijnreparatie Tongelre Eindhoven | 48u service</title>
      <meta
        name="description"
        content="Kozijn, raam en schuifpui reparatie in Tongelre, Lakerlopen en 't Hofke. Vaste tarieven, 10 jaar garantie, ISDE-subsidie hulp."
      />
      <meta property="og:title" content="Kozijnreparatie Tongelre Eindhoven | 48u service" />
      <meta property="og:description" content="Kozijn, raam en schuifpui reparatie in Tongelre, Lakerlopen en 't Hofke. Vaste tarieven, 10 jaar garantie, ISDE-subsidie hulp." />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://ramen-repair-eindhoven.nl/wijken/tongelre/" />
      <meta property="og:image" content="/og/og-wijken.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/wijken/tongelre/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/wijken/tongelre/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/wijken/tongelre/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Kozijnreparatie Tongelre Eindhoven | 48u service" />
      <meta name="twitter:description" content="Kozijn, raam en schuifpui reparatie in Tongelre, Lakerlopen en 't Hofke." />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />
      <SectionHero />
      <SectionOverTongelre />
      <SectionDiensten />
      <SectionWaaromWij />
      <SectionWijkenBinnen />
      <SectionCTA />
      <Footer />
      <StickyMobile />
    </main>
  );
}